import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, Field, TextInput } from "./kit";

const STATUSES = ["New", "Contacted", "In Progress", "Closed"];

const statusColor = {
  New: "bg-cyan-400/15 text-cyan-300 border-cyan-400/30",
  Contacted: "bg-blue-400/15 text-blue-300 border-blue-400/30",
  "In Progress": "bg-amber-400/15 text-amber-300 border-amber-400/30",
  Closed: "bg-slate-400/15 text-slate-300 border-slate-400/30",
};

function AvailabilityCard() {
  const [blocked, setBlocked] = useState([]);
  const [slots, setSlots] = useState([]);
  const [newDate, setNewDate] = useState("");
  const [newSlot, setNewSlot] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/admin/content/availability").then(({ data }) => {
      setBlocked(data.data.blocked_dates || []);
      setSlots(data.data.time_slots || []);
      setLoaded(true);
    }).catch(() => {});
  }, []);

  const save = (nextBlocked, nextSlots) => {
    setBlocked(nextBlocked);
    setSlots(nextSlots);
    api.put("/admin/content/availability", { data: { blocked_dates: nextBlocked, time_slots: nextSlots } })
      .then(() => toast.success("Availability updated"))
      .catch(() => toast.error("Update failed"));
  };

  if (!loaded) return null;
  return (
    <Card title="Booking Availability" desc="Block dates and set the bookable time slots. Default is all hours, 7 days a week.">
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <Field label="Blocked dates">
            <div className="flex gap-2">
              <TextInput type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
              <button
                className="btn btn-sm btn-primary flex-shrink-0"
                onClick={() => { if (newDate && !blocked.includes(newDate)) save([...blocked, newDate], slots); setNewDate(""); }}
                data-testid="availability-add-blocked-date"
              >
                Block
              </button>
            </div>
          </Field>
          <div className="mt-3 flex flex-wrap gap-2">
            {blocked.length === 0 && <span className="text-xs text-slate-500">No dates blocked — every date is bookable.</span>}
            {blocked.map((d) => (
              <span key={d} className="inline-flex items-center gap-1.5 bg-[#0A0F2C] border border-white/10 rounded-full pl-3 pr-1.5 py-1 text-xs text-slate-200" data-testid={`availability-blocked-${d}`}>
                {d}
                <button onClick={() => save(blocked.filter((x) => x !== d), slots)} className="text-slate-500 hover:text-red-400 px-1" aria-label={`Unblock ${d}`}>✕</button>
              </span>
            ))}
          </div>
        </div>
        <div>
          <Field label="Available time slots">
            <div className="flex gap-2">
              <TextInput type="time" value={newSlot} onChange={(e) => setNewSlot(e.target.value)} />
              <button
                className="btn btn-sm btn-primary flex-shrink-0"
                onClick={() => { if (newSlot && !slots.includes(newSlot)) save(blocked, [...slots, newSlot].sort()); setNewSlot(""); }}
                data-testid="availability-add-slot"
              >
                Add
              </button>
            </div>
          </Field>
          <div className="mt-3 flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            {slots.map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5 bg-[#0A0F2C] border border-white/10 rounded-full pl-3 pr-1.5 py-1 text-xs text-slate-200">
                {s}
                <button onClick={() => save(blocked, slots.filter((x) => x !== s))} className="text-slate-500 hover:text-red-400 px-1" aria-label={`Remove ${s}`}>✕</button>
              </span>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("All");

  const load = () => api.get("/admin/leads").then(({ data }) => setLeads(data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    await api.patch(`/admin/leads/${id}`, { status });
    toast.success(`Marked as ${status}`);
  };

  const filtered = filter === "All" ? leads : leads.filter((l) => l.status === filter);

  return (
    <div className="space-y-6" data-testid="admin-lead-inbox">
      <AvailabilityCard />
      <div className="bg-[#0E1642] border border-white/10 rounded-2xl p-6 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <h2 className="font-display font-extrabold text-white text-xl">Lead Inbox</h2>
          <div className="flex gap-2 flex-wrap">
            {["All", ...STATUSES].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${filter === s ? "bg-[#00BFFF] text-[#06122B]" : "bg-[#0A0F2C] text-slate-300 border border-white/10 hover:border-cyan-400/40"}`}
                data-testid={`lead-filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]" data-testid="admin-lead-table">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase tracking-wide border-b border-white/10">
                <th className="pb-3 pr-4 font-semibold">Name</th>
                <th className="pb-3 pr-4 font-semibold">Email</th>
                <th className="pb-3 pr-4 font-semibold">Country</th>
                <th className="pb-3 pr-4 font-semibold">Service</th>
                <th className="pb-3 pr-4 font-semibold">Package</th>
                <th className="pb-3 pr-4 font-semibold">Date</th>
                <th className="pb-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`lead-row-${l.id}`}>
                  <td className="py-3 pr-4 font-semibold text-white whitespace-nowrap">{l.name}</td>
                  <td className="py-3 pr-4 text-slate-300">{l.email}</td>
                  <td className="py-3 pr-4 text-slate-300 whitespace-nowrap">{l.country || "—"}</td>
                  <td className="py-3 pr-4 text-slate-300">{l.type === "quote" ? `Quote — ${l.service || l.package}` : l.service || "—"}</td>
                  <td className="py-3 pr-4 text-slate-300">{l.package || "—"}</td>
                  <td className="py-3 pr-4 text-slate-400 whitespace-nowrap">{l.date ? `${l.date} ${l.time || ""}` : new Date(l.created_at).toLocaleDateString()}</td>
                  <td className="py-3">
                    <select
                      value={l.status || "New"}
                      onChange={(e) => setStatus(l.id, e.target.value)}
                      className={`text-xs font-semibold rounded-full px-3 py-1.5 border outline-none cursor-pointer ${statusColor[l.status || "New"]}`}
                      data-testid={`lead-status-${l.id}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="py-10 text-center text-slate-500">No leads here yet. Bookings and quote requests land in this inbox.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export { AvailabilityCard };
